import { useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useMediaQuery } from '@mui/material'
import Axios from '../../api'
import { ErrorModalContext } from '../../context/errorModalContext'

const useOpenDocument = (url: string, filename?: string) => {
  const { setShowModal, setErrorMessage } = useContext(ErrorModalContext)
  const { getAccessTokenSilently } = useAuth0()
  const isMobile = useMediaQuery('(max-width:800px)')
  
  const handleOpen = async () => {
    const token = await getAccessTokenSilently()
    
    try {
      const response = await Axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      })
     
      if (response.data && response.data.content) {
        const documentName = response.data.name || filename || 'document'
        
        try {
          // Convert base64 to blob for better browser compatibility
          const byteCharacters = atob(response.data.content)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: response.data.mime })
          
          // Check if blob was created successfully
          if (blob.size === 0) {
            throw new Error('Empty blob created')
          }
          
          const fileURL = URL.createObjectURL(blob)
          
          if (isMobile) {
            // On mobile, always download
            const downloadLink = document.createElement('a')
            downloadLink.href = fileURL
            downloadLink.download = documentName
            document.body.appendChild(downloadLink)
            downloadLink.click()
            document.body.removeChild(downloadLink)
            URL.revokeObjectURL(fileURL)
          } else {
            // On desktop, try to open in new tab
            const newWindow = window.open(fileURL, '_blank')
            if (newWindow) {
              newWindow.focus()
              
              // Check if window is blank after a short delay, fallback to download
              setTimeout(() => {
                try {
                  // If we can't access the window or it's blank, trigger download
                  if (!newWindow.document || newWindow.document.body.children.length === 0) {
                    newWindow.close()
                    const downloadLink = document.createElement('a')
                    downloadLink.href = fileURL
                    downloadLink.download = documentName
                    downloadLink.click()
                  }
                } catch (e) {
                  // Cross-origin error means it loaded successfully
                }
                URL.revokeObjectURL(fileURL)
              }, 500)
            } else {
              // Popup blocked, fallback to download
              const downloadLink = document.createElement('a')
              downloadLink.href = fileURL
              downloadLink.download = documentName
              downloadLink.click()
              URL.revokeObjectURL(fileURL)
            }
          }
        } catch (blobError) {
          // If blob creation fails, fallback to simple download with data URL
          console.error('Blob creation failed, using data URL fallback:', blobError)
          const dataURL = 'data:' + response.data.mime + ';base64,' + response.data.content
          const downloadLink = document.createElement('a')
          downloadLink.href = dataURL
          downloadLink.download = documentName
          downloadLink.click()
        }
      } else {
        setShowModal(true)
        setErrorMessage('Le document est vide ou n\'a pas pu être chargé.')
      }
    } catch (error) {
      setShowModal(true)
      setErrorMessage('Une erreur est survenue lors de l\'ouverture du fichier.')
    }
  }
  
  return [handleOpen] as const
}

export default useOpenDocument
